'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { LazyImage } from '@/components/ui/LazyImage';
import { HomePageSkeleton } from '@/components/ui/Skeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// ============================================
// CONSTANTS
// ============================================

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.neotral.org';
const CACHE_KEY = 'neotral_clusters_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const COUNTRY_KEY = 'neotral_country';

// ============================================
// COUNTRY CONFIG
// ============================================

type Country = 'ES' | 'US' | 'UK' | 'DE';

const COUNTRIES: { code: Country; flag: string; name: string }[] = [
  { code: 'ES', flag: '🇪🇸', name: 'España' },
  { code: 'US', flag: '🇺🇸', name: 'USA' },
  { code: 'UK', flag: '🇬🇧', name: 'UK' },
  { code: 'DE', flag: '🇩🇪', name: 'Deutschland' },
];

// ============================================
// TYPES
// ============================================

interface Cluster {
  id: string;
  headline: string;
  topic: string;
  article_ids: string;
  media_count: number;
  importance_score: number;
  silence_type?: string | null;
  synthesis?: string | null;
  updated_at: string;
  image_url?: string | null;
}

interface ParsedSynthesis {
  headline: string;
  summary: string;
}

interface CacheData {
  data: Cluster[];
  timestamp: number;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function parseSynthesis(cluster: Cluster): ParsedSynthesis | null {
  if (!cluster.synthesis) return null;
  try {
    return JSON.parse(cluster.synthesis);
  } catch {
    return null;
  }
}

function getHeadline(cluster: Cluster): string {
  const synthesis = parseSynthesis(cluster);
  return synthesis?.headline || cluster.headline || cluster.topic;
}

function getCachedData(): Cluster[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp }: CacheData = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedData(data: Cluster[]): void {
  if (typeof window === 'undefined') return;
  try {
    const cacheData: CacheData = { data, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // Ignore storage errors
  }
}

// ============================================
// COMPONENTS
// ============================================

function NewsCard({ cluster, featured = false, index = 0 }: { cluster: Cluster; featured?: boolean; index?: number }) {
  const headline = getHeadline(cluster);
  const delay = featured ? 0 : index * 50;
  
  return (
    <article 
      className={`border-b border-stone-300 transition-all duration-200 ease-out opacity-0 animate-fade-in ${
        featured ? 'pb-6 mb-6' : 'py-4'
      } ${!featured ? 'hover:bg-stone-50/80 hover:border-stone-400 hover:pl-2' : ''}`}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
    >
      <Link href={`/cluster/${cluster.id}`} className="block group">
        <div className={featured && cluster.image_url ? 'md:flex md:gap-6' : ''}>
          {/* Featured image with lazy loading */}
          {featured && cluster.image_url && (
            <div className="md:w-1/3 mb-4 md:mb-0 overflow-hidden rounded-lg relative">
              <LazyImage 
                src={cluster.image_url} 
                alt=""
                aspectRatio="video"
                className="grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-500 ease-out"
                priority={featured}
              />
              {/* Salmon tint overlay */}
              <div className="absolute inset-0 bg-[#E8D4C4] mix-blend-multiply opacity-40 group-hover:opacity-0 transition-opacity duration-500 pointer-events-none rounded-lg" />
            </div>
          )}
          
          <div className={featured && cluster.image_url ? 'md:w-2/3' : ''}>
            <h2 className={`font-serif group-hover:text-stone-600 transition-colors duration-150 line-clamp-2 ${
              featured 
                ? 'text-xl sm:text-2xl md:text-3xl font-bold mb-3' 
                : 'text-base sm:text-lg font-semibold mb-2'
            }`}>
              {headline}
            </h2>
            
            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-stone-500">
              <span className="inline-flex items-center gap-1.5 font-mono bg-stone-100 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full"></span>
                {cluster.media_count} fuentes
              </span>
              {cluster.silence_type && (
                <span className="inline-flex items-center gap-1 text-amber-700 font-medium bg-amber-50 px-2 py-0.5 rounded-full">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {cluster.silence_type === 'left_only' ? 'Solo izq.' : 'Solo der.'}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

function BriefItem({ cluster, index = 0 }: { cluster: Cluster; index?: number }) {
  const headline = getHeadline(cluster);
  
  return (
    <li 
      className="py-3 border-b border-stone-200 last:border-0 opacity-0 animate-fade-in"
      style={{ animationDelay: `${300 + index * 50}ms`, animationFillMode: 'forwards' }}
    >
      <Link 
        href={`/cluster/${cluster.id}`} 
        className="group flex items-start gap-2 font-serif text-sm hover:text-stone-600 transition-all duration-150"
      >
        <span className="text-stone-300 group-hover:text-red-500 transition-colors mt-0.5">→</span>
        <span className="line-clamp-2 group-hover:translate-x-0.5 transition-transform duration-150">{headline}</span>
      </Link>
    </li>
  );
}

function ErrorMessage({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="text-center py-16 animate-fade-in">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-50 rounded-full mb-4">
        <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <p className="text-stone-700 font-serif text-lg mb-2">Algo salió mal</p>
      <p className="text-amber-700 text-sm mb-6">{message}</p>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-900 text-white rounded-lg font-medium text-sm hover:bg-stone-700 active:scale-[0.98] transition-all duration-150"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reintentar
        </button>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 animate-fade-in">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 rounded-full mb-4">
        <svg className="w-8 h-8 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      </div>
      <p className="text-stone-600 font-serif text-lg mb-2">
        Sin noticias multi-fuente
      </p>
      <p className="text-stone-400 text-sm">
        Vuelve pronto para ver historias cubiertas por múltiples medios.
      </p>
    </div>
  );
}

function SilencedSection({ clusters }: { clusters: Cluster[] }) {
  if (clusters.length === 0) return null;
  
  return (
    <section className="mt-10 pt-6 border-t-2 border-stone-800 opacity-0 animate-fade-in" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center justify-center w-6 h-6 bg-amber-100 rounded-full">
          <svg className="w-3.5 h-3.5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
        </div>
        <h2 className="text-xs font-mono uppercase tracking-wider text-amber-700">
          Mapa del Silencio
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {clusters.slice(0, 4).map((cluster, i) => {
          const headline = getHeadline(cluster);
          const isLeft = cluster.silence_type === 'left_only';
          return (
            <Link 
              key={cluster.id}
              href={`/cluster/${cluster.id}`}
              className={`group block p-4 rounded-lg border-l-4 transition-all duration-200 hover:shadow-md opacity-0 animate-slide-in ${
                isLeft 
                  ? 'bg-red-50/50 border-red-400 hover:bg-red-50' 
                  : 'bg-blue-50/50 border-blue-400 hover:bg-blue-50'
              }`}
              style={{ animationDelay: `${500 + i * 100}ms`, animationFillMode: 'forwards' }}
            >
              <span className={`inline-flex items-center gap-1.5 text-xs font-mono uppercase ${isLeft ? 'text-red-600' : 'text-blue-600'}`}>
                <span className={`w-2 h-2 rounded-full ${isLeft ? 'bg-red-500' : 'bg-blue-500'}`}></span>
                {isLeft ? 'Solo izquierda' : 'Solo derecha'}
              </span>
              <p className="font-serif mt-2 line-clamp-2 text-stone-800 group-hover:text-stone-600 transition-colors">{headline}</p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

function HomePageContent() {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  // Initialize country from localStorage (lazy initial state)
  const [country, setCountry] = useState<Country>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(COUNTRY_KEY) as Country;
      if (saved && ['ES', 'US', 'UK', 'DE'].includes(saved)) {
        return saved;
      }
    }
    return 'ES';
  });
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const changeCountry = (newCountry: Country) => {
    setCountry(newCountry);
    localStorage.setItem(COUNTRY_KEY, newCountry);
    setShowCountryPicker(false);
    // Clear cache, reset pagination, and refetch
    localStorage.removeItem(CACHE_KEY);
    setClusters([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
  };

  const fetchClusters = useCallback(async (useCache = true, loadMore = false) => {
    try {
      setError(null);
      if (loadMore) {
        setLoadingMore(true);
      }
      
      // Try cache first (only for initial load)
      if (useCache && !loadMore) {
        const cached = getCachedData();
        if (cached && cached.length > 0) {
          setClusters(cached);
          setLoading(false);
          // Fetch fresh data in background
          fetchClusters(false);
          return;
        }
      }

      const currentOffset = loadMore ? offset + 20 : 0;
      const res = await fetch(`${API_URL}/api/news?limit=20&offset=${currentOffset}&country=${country}`);
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      const newClusters = data.data?.clusters || [];
      
      if (loadMore) {
        setClusters(prev => [...prev, ...newClusters]);
        setOffset(currentOffset);
      } else {
        setClusters(newClusters);
        setCachedData(newClusters);
        setOffset(0);
      }
      
      setHasMore(data.data?.hasMore ?? newClusters.length === 20);
      
      // Update country if server detected it differently
      if (data.data?.detectedCountry && !localStorage.getItem(COUNTRY_KEY)) {
        setCountry(data.data.detectedCountry as Country);
      }
      // Set last update time
      if (newClusters.length > 0 && !loadMore) {
        setLastUpdate(new Date(newClusters[0].updated_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }));
      }
    } catch {
      if (clusters.length === 0) {
        setError('Error cargando noticias. Comprueba tu conexión.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [clusters.length, country, offset]);

  const loadMoreClusters = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchClusters(false, true);
    }
  }, [fetchClusters, loadingMore, hasMore]);

  useEffect(() => {
    fetchClusters();
  }, [country]);

  // Format today's date
  const today = new Date().toLocaleDateString('es-ES', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Sort and filter clusters
  const sorted = [...clusters].sort((a, b) => (b.importance_score || 0) - (a.importance_score || 0));
  // Show multi-source first, but fallback to single-source if none
  const multiSource = sorted.filter(c => c.media_count >= 2);
  const displayClusters = multiSource.length > 0 ? multiSource : sorted.slice(0, 10);
  const silenced = sorted.filter(c => c.silence_type && c.media_count >= 2);
  
  // Split for layout
  const featured = displayClusters[0];
  const main = displayClusters.slice(1, 5);
  const sidebar = displayClusters.slice(5, 10);

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Top bar: Last update + Edition selector */}
          <div className="flex items-center justify-between py-2 border-b border-stone-200">
            {/* Last update time - most important info */}
            <div className="flex items-center gap-2">
              {lastUpdate ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-xs text-stone-600">
                    Actualizado {lastUpdate}
                  </span>
                </>
              ) : (
                <span className="text-xs text-stone-400">Cargando...</span>
              )}
            </div>
            
            {/* Edition Selector */}
            <div className="relative">
              <button 
                onClick={() => setShowCountryPicker(!showCountryPicker)}
                className="flex items-center gap-1.5 px-2 py-1 text-xs text-stone-600 hover:text-stone-900 transition-colors"
              >
                <span className="font-serif">Edición {COUNTRIES.find(c => c.code === country)?.name}</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showCountryPicker ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
              </button>
              {showCountryPicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowCountryPicker(false)} />
                  <div className="absolute top-full right-0 mt-1 bg-white border border-stone-300 shadow-lg z-50 rounded-lg overflow-hidden min-w-[160px]">
                    {COUNTRIES.map(c => (
                      <button
                        key={c.code}
                        onClick={() => changeCountry(c.code)}
                        className={`flex items-center gap-2 px-3 py-2 w-full text-sm hover:bg-stone-50 transition-colors ${
                          country === c.code ? 'bg-stone-100 font-medium' : ''
                        }`}
                      >
                        <span>{c.flag}</span>
                        <span className="font-serif">Edición {c.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Masthead - clean, centered */}
          <div className="text-center py-4">
            <Link href="/">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-black tracking-tight text-stone-900 hover:text-stone-700 transition-colors">
                NEOTRAL
              </h1>
            </Link>
            <p className="text-xs sm:text-sm text-stone-500 italic mt-1 font-serif">
              Todas las versiones. Tú decides.
            </p>
          </div>
          
          {/* Navigation */}
          <nav className="flex justify-center gap-6 sm:gap-8 py-2 border-t border-stone-200 text-sm overflow-x-auto no-scrollbar">
            <Link href="/" className="font-serif font-semibold text-stone-900 whitespace-nowrap">
              Hoy
            </Link>
            <Link href="/radar" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Radar
            </Link>
            <Link href="/medios" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Medios
            </Link>
            <Link href="/silenciadas" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Silenciadas
            </Link>
            <Link href="/algoritmo" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Algoritmo
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 page-enter">
        {loading ? (
          <HomePageSkeleton />
        ) : error ? (
          <ErrorMessage message={error} onRetry={() => fetchClusters(false)} />
        ) : displayClusters.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Featured story */}
            {featured && <NewsCard cluster={featured} featured />}

            {/* Main content grid */}
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-8">
              {/* Main column */}
              <div className="md:col-span-2">
                <h2 className="text-xs font-mono uppercase tracking-wider text-stone-400 border-b border-stone-800 pb-2 mb-4">
                  Destacadas
                </h2>
                {main.map((cluster, i) => (
                  <NewsCard key={cluster.id} cluster={cluster} index={i + 1} />
                ))}
              </div>

              {/* Sidebar */}
              <aside className="hidden sm:block">
                <h2 className="text-xs font-mono uppercase tracking-wider text-stone-400 border-b border-stone-800 pb-2 mb-4">
                  Más noticias
                </h2>
                <ul className="list-none p-0 m-0">
                  {sidebar.map((cluster, i) => (
                    <BriefItem key={cluster.id} cluster={cluster} index={i} />
                  ))}
                </ul>
              </aside>
            </div>

            {/* Mobile: show more news inline */}
            {sidebar.length > 0 && (
              <div className="sm:hidden mt-6 border-t border-stone-300 pt-4">
                <h2 className="text-xs font-mono uppercase tracking-wider text-stone-400 mb-4">
                  Más noticias
                </h2>
                {sidebar.map(cluster => (
                  <NewsCard key={cluster.id} cluster={cluster} />
                ))}
              </div>
            )}

            {/* Silenced news section */}
            <SilencedSection clusters={silenced} />

            {/* Load More button */}
            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={loadMoreClusters}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-lg font-medium text-sm hover:bg-stone-700 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Cargando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
                      </svg>
                      Noticias anteriores
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-stone-800 mt-12 bg-[#FDF6E3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Logo/Brand */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="w-2 h-2 rounded-full bg-stone-400"></span>
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              </div>
              <span className="font-serif font-bold text-stone-700">NEOTRAL</span>
            </div>
            
            {/* Tagline */}
            <p className="text-xs text-stone-400 font-serif italic">
              Periodismo comparado · Todas las versiones
            </p>
            
            {/* Copyright */}
            <p className="text-xs text-stone-400 font-mono">
              © {new Date().getFullYear()} Fundación MAFT
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function HomePage() {
  return (
    <ErrorBoundary>
      <HomePageContent />
    </ErrorBoundary>
  );
}
